import noop from 'lodash/noop';
import { useEffect, useMemo } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { match } from 'ts-pattern';

import { DatePickerField, TypeaheadTimePickerField } from 'lib/iterable-hook-form/FormField';
import { Caption } from 'modules/campaigns/editor/components/Sections/OptimizeSection/OptimizeSection.styles';
import { ErrorText } from 'modules/campaigns/editor/styles';
import { ScheduleFormState } from 'modules/campaigns/editor/types';
import { ErrorBanner, WarningBanner } from 'ui/banner';
import { FormField } from 'ui/formField';
import { spacing } from 'ui/theme';
import { RowWrapper } from '../ScheduleCampaignModal.styled';
import {
  getCampaignScheduleDateRange,
  getScheduleCampaignTargetTimeZone,
} from '../ScheduleCampaignModal.utils';
import { useSendTimeIsWithinQuietHours } from '../hooks';

export const StyledWarningBanner = styled(WarningBanner)`
  margin: ${spacing.fiveEights} 0;
`;

export const StyledErrorBanner = styled(ErrorBanner)`
  margin: ${spacing.fiveEights} 0;
`;

const StyledCaption = styled(Caption)`
  margin-bottom: ${spacing.half};
`;

export const StartDateAndTime = () => {
  const { errors } = useFormState<ScheduleFormState>();
  const { watch, trigger } = useFormContext();
  const [projectTimeZone, startingTimeZone, scheduleType] = useWatch({
    name: ['projectTimeZone', 'startingTimeZone', 'scheduleType'],
  });

  useEffect(() => {
    watch((_, { name }) => {
      match(name)
        .with('startDate', () => {
          trigger('startTime');
          trigger('endDate');
        })
        .otherwise(noop);
    });
  }, [watch, trigger]);

  const { startDate: startDateError, startTime: startTimeError } = errors;
  const validDateRange = useMemo(() => {
    const targetTz = getScheduleCampaignTargetTimeZone(
      projectTimeZone,
      startingTimeZone,
      scheduleType,
    );

    return getCampaignScheduleDateRange(targetTz);
  }, [projectTimeZone, startingTimeZone, scheduleType]);

  const { isSendTimeWithinQuietHoursAndSkipped, isSendTimeWithinQuietHoursAndNotSkipped } =
    useSendTimeIsWithinQuietHours();

  return (
    <>
      <FormField labelText="Start date and time">
        <StyledCaption>Choose a date up to 21 days in advance</StyledCaption>
        <RowWrapper>
          <DatePickerField
            inputId="scheduleCampaignStartDateAndTime"
            name="startDate"
            validDateRange={validDateRange}
          />
          <TypeaheadTimePickerField
            increment={15}
            inputClockFormat="12-hour"
            name="startTime"
            isError={isSendTimeWithinQuietHoursAndSkipped}
          />
        </RowWrapper>
        {(startDateError?.message || startTimeError?.message) && (
          <ErrorText>{startDateError?.message || startTimeError?.message}</ErrorText>
        )}
      </FormField>
      {isSendTimeWithinQuietHoursAndNotSkipped && (
        <StyledWarningBanner>
          Your selected send time is during your quiet hours window—this campaign won&apos;t be sent
          until after quiet hours ends. To send this campaign sooner, choose a different send time
          or update your quiet hours settings.
        </StyledWarningBanner>
      )}
      {isSendTimeWithinQuietHoursAndSkipped && (
        <StyledErrorBanner>
          Your selected send time is during your quiet hours window—this campaign won&apos;t send.
          Choose a different send time or update your quiet hours settings.
        </StyledErrorBanner>
      )}
    </>
  );
};
